import type {
  RequirementLevel,
  RunFeedPreviewColumn,
  RunFeedPreviewRow,
  RunSkuPreviewAttribute,
} from "@nova-ai/shared"

/** Product information belongs only to current placeholders, never emitted cells. */
export function buildRowlessInspection(
  row: RunFeedPreviewRow | null,
  columns: readonly RunFeedPreviewColumn[],
  attributes: readonly RunSkuPreviewAttribute[] | undefined,
  isHistorical: boolean
) {
  const variantAttributes = new Map<string, RunSkuPreviewAttribute[]>()
  if (!row?.isPlaceholder || isHistorical || !attributes) {
    return { row, columns, variantAttributes }
  }
  const values = { ...row.values }
  const inspectionColumns = new Map(columns.map((column) => [column.code, column]))
  for (const attribute of attributes) {
    if (!inspectionColumns.has(attribute.attrCode)) {
      inspectionColumns.set(attribute.attrCode, {
        code: attribute.attrCode,
        label: labelForAttribute(attribute.attrCode, undefined),
      })
    }
    if (attribute.scope === "config") {
      values[attribute.attrCode] = attribute.rewrittenValue ?? ""
    } else {
      const variants = variantAttributes.get(attribute.attrCode) ?? []
      variants.push(attribute)
      variantAttributes.set(attribute.attrCode, variants)
    }
  }
  return { row: { ...row, values }, columns: [...inspectionColumns.values()], variantAttributes }
}

export const REQUIREMENT_ORDER: RequirementLevel[] = [
  "required",
  "recommended",
  "optional",
  "not_needed",
]

export type RequirementCoverageItem = {
  code: string
  label: string
}

export type RequirementCoverageGroup = {
  level: RequirementLevel
  filled: number
  total: number
  missing: RequirementCoverageItem[]
}

export function buildRequirementCoverage(
  requirementByCode: Record<string, RequirementLevel> | undefined,
  values: Record<string, string>,
  columns: readonly RunFeedPreviewColumn[]
): RequirementCoverageGroup[] {
  if (!requirementByCode) {
    return []
  }
  const labelByCode = new Map(columns.map((column) => [column.code, column.label]))
  const counts = new Map<RequirementLevel, RequirementCoverageGroup>()
  for (const [code, level] of Object.entries(requirementByCode)) {
    const current = counts.get(level) ?? { level, filled: 0, total: 0, missing: [] }
    current.total += 1
    if ((values[code] ?? "").trim().length > 0) {
      current.filled += 1
    } else {
      current.missing.push({ code, label: labelForAttribute(code, labelByCode.get(code)) })
    }
    counts.set(level, current)
  }
  return REQUIREMENT_ORDER.flatMap((level) => {
    const group = counts.get(level)
    return group ? [group] : []
  })
}

function labelForAttribute(code: string, explicitLabel: string | undefined): string {
  if (explicitLabel && explicitLabel.trim().length > 0) {
    return explicitLabel
  }
  return code
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/^\w/, (first) => first.toUpperCase())
}
